import React from "react";
import { HREmployee } from "../../types/hr";
import EmployeeAvatar from "./EmployeeAvatar";
import {
  Modal,
  Card,
  Row,
  Col,
  Descriptions,
  Tag,
  Avatar,
  Button,
  Typography,
  Statistic,
  Divider,
} from "antd";
import { UserOutlined, EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { Title, Text } = Typography;

interface EmployeeDetailsProps {
  employee: HREmployee;
  onClose: () => void;
  onEdit: () => void;
}

const EmployeeDetails: React.FC<EmployeeDetailsProps> = ({
  employee,
  onClose,
  onEdit,
}) => {
  const formatDate = (dateString: string) => {
    return dayjs(dateString).format("DD/MM/YYYY");
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("fr-BF", {
      style: "currency",
      currency: "XOF",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusTag = (status: string) => {
    const statusConfig = {
      active: { color: "green", text: "Actif" },
      inactive: { color: "orange", text: "Inactif" },
      terminated: { color: "red", text: "Terminé" },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || {
      color: "default",
      text: status,
    };

    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getGenderText = (gender: string | undefined) => {
    switch (gender) {
      case "M":
        return "Masculin";
      case "F":
        return "Féminin";
      default:
        return gender || "-";
    }
  };

  return (
    <Modal
      title="Détails de l'employé"
      open={true}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Fermer
        </Button>,
        <Button
          key="edit"
          type="primary"
          icon={<EditOutlined />}
          onClick={onEdit}
        >
          Modifier
        </Button>,
      ]}
      width={1000}
      destroyOnClose
    >
      <Row gutter={[24, 24]}>
        {/* Profile Card */}
        <Col xs={24} lg={8}>
          <Card>
            <div style={{ textAlign: "center" }}>
              <EmployeeAvatar
                employee={employee}
                size={80}
                style={{ marginBottom: 16 }}
              />

              <Title level={4} style={{ marginBottom: 8 }}>
                {employee.first_name} {employee.last_name}
              </Title>

              <Text
                type="secondary"
                style={{ display: "block", marginBottom: 16 }}
              >
                {employee.position_title || "Poste non défini"}
              </Text>

              <div style={{ marginBottom: 16 }}>
                {getStatusTag(employee.status)}
              </div>

              {/* Salaire de base sera affiché depuis les contrats actifs */}
              <Text type="secondary" style={{ display: "block" }}>
                Informations salariales disponibles via les contrats
              </Text>
            </div>
          </Card>
        </Col>

        {/* Details */}
        <Col xs={24} lg={16}>
          <Card title="Informations personnelles" style={{ marginBottom: 16 }}>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Numéro d'employé">
                {employee.employee_number}
              </Descriptions.Item>
              <Descriptions.Item label="Genre">
                {getGenderText(employee.gender)}
              </Descriptions.Item>
              <Descriptions.Item label="Date de naissance">
                {employee.date_of_birth
                  ? formatDate(employee.date_of_birth)
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Date d'embauche">
                {formatDate(employee.hire_date)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Contact" style={{ marginBottom: 16 }}>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Email">
                {employee.email || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Téléphone">
                {employee.phone || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Adresse" span={2}>
                {employee.address || "-"}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card
            title="Informations professionnelles"
            style={{ marginBottom: 16 }}
          >
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Département">
                {employee.department_name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Poste">
                {employee.position_title || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Type de contrat">
                {employee.contract_type || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Statut">
                {getStatusTag(employee.status)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Row gutter={16}>
            <Col span={12}>
              <Card title="Contact d'urgence" style={{ marginBottom: 16 }}>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Nom">
                    {employee.emergency_contact_name || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Téléphone">
                    {employee.emergency_contact_phone || "-"}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>

            <Col span={12}>
              <Card title="Informations bancaires" style={{ marginBottom: 16 }}>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Banque">
                    {employee.bank_name || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Numéro de compte">
                    {employee.bank_account || "-"}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
          </Row>

          <Card title="Documents officiels">
            <Descriptions column={2} size="small">
              <Descriptions.Item label="CNI/Passeport">
                {employee.national_id || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Numéro CNSS">
                {employee.social_security_number || "-"}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <Text type="secondary" style={{ fontSize: 12 }}>
              Créé le {formatDate(employee.created_at)}
              {employee.updated_at !== employee.created_at && (
                <> • Modifié le {formatDate(employee.updated_at)}</>
              )}
            </Text>
          </Card>
        </Col>
      </Row>
    </Modal>
  );
};

export default EmployeeDetails;
